/**
 * Saved Filters Utility
 *
 * Manages saving, loading, and deleting user filter presets
 * Stores in localStorage for quick access
 */

export interface ArbitrageFilters {
  type: 'arbitrage';
  name: string;
  selectedSports: string[];
  selectedRegions: string[];
  selectedMarkets: string[];
  selectedPlayerPropMarkets?: string[];
  selectedBookmakers: string[];
  selectedGameStatus: string[];
  minProfit: number;
  maxProfit: number;
}

export interface EVFilters {
  type: 'ev';
  name: string;
  selectedSports: string[];
  selectedMarkets?: string[];
  selectedBookmakers: string[];
  selectedGameStatus: string[];
  minEV: number;
  maxEV: number;
  minProbability: number;
  maxProbability: number;
}

export type SavedFilter = ArbitrageFilters | EVFilters;

const STORAGE_KEY = 'arber-saved-filters';

/**
 * Get all saved filters from localStorage
 */
export function getSavedFilters(): SavedFilter[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error loading saved filters:', error);
    return [];
  }
}

/**
 * Get saved filters by type
 */
export function getSavedFiltersByType(type: 'arbitrage' | 'ev'): SavedFilter[] {
  return getSavedFilters().filter(f => f.type === type);
}

/**
 * Save a new filter preset
 */
export function saveFilter(filter: SavedFilter): boolean {
  try {
    const filters = getSavedFilters();

    // Check if name already exists
    const existingIndex = filters.findIndex(
      f => f.name.toLowerCase() === filter.name.toLowerCase() && f.type === filter.type
    );

    if (existingIndex >= 0) {
      // Replace existing filter
      filters[existingIndex] = filter;
    } else {
      // Add new filter
      filters.push(filter);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    return true;
  } catch (error) {
    console.error('Error saving filter:', error);
    return false;
  }
}

/**
 * Delete a saved filter by name and type
 */
export function deleteFilter(name: string, type: 'arbitrage' | 'ev'): boolean {
  try {
    const filters = getSavedFilters();
    const filtered = filters.filter(
      f => !(f.name.toLowerCase() === name.toLowerCase() && f.type === type)
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting filter:', error);
    return false;
  }
}

/**
 * Check if a filter name already exists
 */
export function filterNameExists(name: string, type: 'arbitrage' | 'ev'): boolean {
  const filters = getSavedFilters();
  return filters.some(
    f => f.name.toLowerCase() === name.toLowerCase() && f.type === type
  );
}
